/**
 * AI Assistant Platform — frontend client.
 *
 * Talks to the ai_assistent FastAPI backend (default: https://ai.solvitcompany.com — Apache proxies /v1, /ai, …).
 * All AI calls (chat completion, product extraction, agents) go through here
 * so provider keys never leave the server.
 *
 * Auth: per-user bearer token from `api_config.ai` (see `getAiConfig()` in
 * hooks/useAiConfig.ts) — same token as the chat widget / admin UI.
 */

import JSDB from '../../fromKotlin/nk';
import { getAiConfig } from '../../hooks/useAiConfig';

// ─── Configuration ───────────────────────────────────────────────────────────

/**
 * Base URL of the ai_assistent backend.
 * Override in production via window.__AI_ASSISTENT_BASE__ or localStorage.
 */
const DEFAULT_BASE = 'https://ai.solvitcompany.com';
function getBase(): string {
    const fromConfig = getAiConfig()?.apiBase?.replace(/\/+$/, '');
    if (fromConfig) return fromConfig;

    if (typeof window !== 'undefined') {
        const w = window as any;
        if (w.__AI_ASSISTENT_BASE__) return String(w.__AI_ASSISTENT_BASE__).replace(/\/+$/, '');
    }
    try {
        const stored = localStorage.getItem('AI_ASSISTENT_BASE');
        if (stored) return stored.replace(/\/+$/, '');
    } catch { /* SSR */ }
    return DEFAULT_BASE;
}

/**
 * Resolve the per-user ai_assistent bearer token.
 *
 * Strategy:
 *  1. window.__AI_ASSISTENT_TOKEN__
 *  2. localStorage.AI_ASSISTENT_TOKEN
 *  3. `api_config.ai` via getAiConfig() (users.yotam / default.token)
 */
export async function getAiAssistentToken(): Promise<string> {
    if (typeof window !== 'undefined') {
        const w = window as any;
        if (w.__AI_ASSISTENT_TOKEN__) return String(w.__AI_ASSISTENT_TOKEN__);
        try {
            const stored = localStorage.getItem('AI_ASSISTENT_TOKEN');
            if (stored) return stored;
        } catch { /* SSR */ }
    }

    const fromConfig = getAiConfig()?.token?.trim();
    if (fromConfig) return fromConfig;

    throw new Error(
        'חסר טוקן AI: הוסף api_config.ai (users / default.token) או הגדר __AI_ASSISTENT_TOKEN__',
    );
}

/**
 * Override the cached token (e.g. for testing). Pass `null` to clear.
 */
export function setAiAssistentToken(token: string | null): void {
    if (typeof window !== 'undefined') {
        if (token) {
            (window as any).__AI_ASSISTENT_TOKEN__ = token;
            try { localStorage.setItem('AI_ASSISTENT_TOKEN', token); } catch { /* */ }
        } else {
            delete (window as any).__AI_ASSISTENT_TOKEN__;
            try { localStorage.removeItem('AI_ASSISTENT_TOKEN'); } catch { /* */ }
        }
    }
}

// ─── GET /me/info (token scopes + identity) ──────────────────────────────────

export interface AiMeInfo {
    user_id: string;
    display_name: string;
    company_id: string | null;
    company_uuid: string | null;
    plan_tier: string;
    scopes: string[];
}

let meInfoCache: { token: string; promise: Promise<AiMeInfo> } | null = null;

export async function aiMeInfo(): Promise<AiMeInfo> {
    const token = await getAiAssistentToken();
    if (meInfoCache?.token === token) {
        return meInfoCache.promise;
    }
    const promise = (async () => {
        const resp = await fetch(`${getBase()}/me/info`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${token}` },
            cache: 'no-store',
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) {
            const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
            throw new Error(`ai_assistent /me/info ${resp.status}: ${detail}`);
        }
        return json as AiMeInfo;
    })();
    meInfoCache = { token, promise };
    promise.catch(() => {
        if (meInfoCache?.token === token) meInfoCache = null;
    });
    return promise;
}

// ─── Generic /ai/complete ────────────────────────────────────────────────────

export type AiTier = 'auto' | 'light' | 'medium' | 'smart' | 'heavy' | 'reasoning';

export interface AiChatMessage {
    role: 'system' | 'user' | 'assistant';
    content: string;
}

export interface AiCompleteOptions {
    messages: AiChatMessage[];
    tier?: AiTier;
    /** Override tier with explicit "provider/model" e.g. "openai/gpt-4.1". */
    model?: string | null;
    temperature?: number;
    max_tokens?: number;
    json_response?: boolean;
    reasoning_effort?: 'low' | 'medium' | 'high' | null;
    image_inputs?: { base64?: string; url?: string; mime_type?: string }[];
    /** Tag for usage analytics, e.g. "automation.invoice_approval". */
    feature?: string;
    /** Provider-specific extra body fields (rare). */
    extras?: Record<string, unknown>;
    /** Per-request timeout (ms). Default 180000 (180s). */
    timeoutMs?: number;
}

export interface AiCompleteResult {
    content: string;
    reasoning_content?: string | null;
    model: string;
    provider: string;
    tier: string;
    tokens: { prompt: number; completion: number; total: number };
    cost_usd: number;
    latency_ms: number;
}

export async function aiComplete(opts: AiCompleteOptions): Promise<AiCompleteResult> {
    const token = await getAiAssistentToken();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), opts.timeoutMs ?? 180_000);
    try {
        const resp = await fetch(`${getBase()}/ai/complete`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                messages: opts.messages,
                tier: opts.tier ?? 'medium',
                model: opts.model ?? null,
                temperature: opts.temperature ?? 0.1,
                max_tokens: opts.max_tokens ?? 1024,
                json_response: Boolean(opts.json_response),
                reasoning_effort: opts.reasoning_effort ?? null,
                image_inputs: opts.image_inputs,
                feature: opts.feature ?? '',
                extras: opts.extras ?? {},
            }),
            signal: controller.signal,
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) {
            const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
            throw new Error(`ai_assistent /ai/complete ${resp.status}: ${detail}`);
        }
        return json as AiCompleteResult;
    } finally {
        clearTimeout(timeout);
    }
}

// ─── /v1/services/product-extraction ─────────────────────────────────────────

export type ExtractionProcess = 'order' | 'supplier_invoice';

export interface ExtractionCandidate {
    productId: number;
    name: string;
    score: number;
    unit?: string;
    availableUnits?: string[];
    priority?: number;
    prevOrdered?: 0 | 1;
    barcode?: string;
    externalId?: string;
    conversionRatio?: number;
    unitPreference?: number;
    previousOrders?: { usedAmount?: number; usedUnit?: string; usedUnitIndex?: number }[];
}

export interface SavedExtractionMatch {
    req_name: string;
    req_unit?: string;
    match_id: number;
    match_name?: string;
    match_unit?: string;
    metadata?: Record<string, unknown>;
}

export interface ProductExtractionRequest {
    process: ExtractionProcess;
    text?: string;
    image?: { base64: string; mime_type?: string };
    knownUnits?: string[];
    candidatesByLine?: Record<string, ExtractionCandidate[]>;
    savedMatches?: SavedExtractionMatch[];
    conversionRateBlocker?: number;
    extractTier?: AiTier;
    matchTier?: AiTier;
    extractModel?: string | null;
    matchModel?: string | null;
    feature?: string;
    debug?: boolean;
}

export interface ExtractedProduct {
    lineId: string;
    productName: string;
    amount: number;
    unit: string | null;
    price: number | null;
    total: number | null;
    barcode?: string | null;
    extId?: string | null;
    confidence: number | null;
    rawText: string | null;
}

export interface ExtractedDocument {
    date: string | null;
    entityName: string | null;
    entityId: string | null;
    total: number | null;
    products: ExtractedProduct[];
}

export interface ExtractionMatch {
    lineId: string;
    productId: number | null;
    unit: string | null;
    amount: number | null;
    price: number | null;
    confidence: number;
    reason: string | null;
}

export interface ProductExtractionResult {
    extracted: ExtractedDocument;
    matches: ExtractionMatch[] | null;
    usage: any[];
    trace?: Record<string, unknown>;
}

export interface ProductMatchingRequest {
    process: ExtractionProcess;
    extractedProducts: ExtractedProduct[];
    candidatesByLine: Record<string, ExtractionCandidate[]>;
    savedMatches?: SavedExtractionMatch[];
    /** ERP client id — loads scoped `ai_match_memory` on the server when useMemory is true. */
    clientId?: string | number | null;
    supplierId?: string | number | null;
    useMemory?: boolean;
    conversionRateBlocker?: number;
    tier?: AiTier;
    model?: string | null;
    feature?: string;
    debug?: boolean;
}

export interface ProductMatchingResult {
    matches: ExtractionMatch[];
    usage: any[];
    trace?: Record<string, unknown>;
}

export async function aiProductMatching(req: ProductMatchingRequest): Promise<ProductMatchingResult> {
    const token = await getAiAssistentToken();
    const resp = await fetch(`${getBase()}/v1/services/product-matching`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            process: req.process,
            extractedProducts: req.extractedProducts,
            candidatesByLine: req.candidatesByLine,
            savedMatches: req.savedMatches,
            clientId: req.clientId != null ? String(req.clientId) : null,
            supplierId: req.supplierId != null ? String(req.supplierId) : null,
            useMemory: req.useMemory !== false,
            conversionRateBlocker: req.conversionRateBlocker ?? 0,
            tier: req.tier ?? 'medium',
            model: req.model ?? null,
            feature: req.feature ?? 'text_to_data.match',
            debug: Boolean(req.debug),
        }),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/services/product-matching ${resp.status}: ${detail}`);
    }
    return json as ProductMatchingResult;
}

// ─── /v1/services/match-products  (smart hybrid matcher) ────────────────────
//
// Same brain as /v1/services/product-extraction but:
//   • can fetch the catalog + previousOrders ITSELF via the user's NihulKav JWT
//     (fast path — caller doesn't have to send anything but text + client_id)
//   • uses persistent ai_match_memory so repeats are free (no LLM)
//   • caller can still hand-feed catalog/savedMatches/candidatesByLine for
//     tighter control (manual mode — what the existing TextToDataDrawer does)
//
// Use this when you want the brain to do the heavy lifting (WhatsApp bot,
// agents). Keep using /product-extraction when you already have all the data
// pre-filtered on the client.

export interface SmartMatchManual {
    /** Full product catalog rows (with priority, unit, unit_2, ext_ref, ...). */
    products?: Array<Record<string, unknown>>;
    /** Legacy savedMatches from NihulKav (used as soft hint). */
    saved_matches?: SavedExtractionMatch[];
    /** Hebrew unit dictionary (יחידה, ק"ג, ארגז…). */
    known_units?: string[];
    /** Pre-filtered candidates per line — skips the brain's fuzzy step. */
    candidates_by_line?: Record<string, ExtractionCandidate[]>;
}

export interface SmartMatchOptions {
    extract_tier?: AiTier;
    match_tier?: AiTier;
    /** Read ai_match_memory before falling back to fuzzy+LLM (default true). */
    use_memory?: boolean;
    /** Auto-store high-confidence LLM matches into memory (default false; UI usually
     *  prefers an explicit /v1/services/match/confirm after user approval). */
    remember_results?: boolean;
    auto_remember_threshold?: number;
    max_candidates?: number;
    conversion_rate_blocker?: number;
    /** When true, products flagged `available=0` (entity priority OR global
     *  catalog) are stripped from candidate lists and stale memory hits.
     *  The matcher returns the next-best AVAILABLE alternative instead. */
    ignore_unavailable?: boolean;
    debug?: boolean;
}

export interface SmartMatchProductsRequest {
    process?: ExtractionProcess;
    /** ERP client id (e.g. nihulkav numeric id as string). */
    client_id?: string | number | null;
    text?: string;
    image?: { base64: string; mime_type?: string };
    manual?: SmartMatchManual;
    options?: SmartMatchOptions;
    feature?: string;
}

export interface SmartProductMatch extends ExtractionMatch {
    productName?: string | null;
    /** "memory" | "llm" — where the match came from. */
    source?: 'memory' | 'llm' | string | null;
}

export interface SmartMatchProductsResult {
    extracted: ExtractedDocument;
    matches: SmartProductMatch[] | null;
    usage: any[];
    warning?: string;
    trace?: Record<string, unknown>;
}

export async function aiMatchProducts(
    req: SmartMatchProductsRequest,
): Promise<SmartMatchProductsResult> {
    const token = await getAiAssistentToken();
    const body = {
        process: req.process ?? 'order',
        client_id: req.client_id != null ? String(req.client_id) : null,
        input: req.image
            ? { image_base64: req.image.base64, mime_type: req.image.mime_type ?? 'image/jpeg' }
            : { text: req.text ?? '' },
        manual: {
            products: req.manual?.products,
            saved_matches: req.manual?.saved_matches,
            known_units: req.manual?.known_units ?? [],
            candidates_by_line: req.manual?.candidates_by_line,
        },
        options: {
            extract_tier: req.options?.extract_tier ?? 'medium',
            match_tier: req.options?.match_tier ?? 'medium',
            use_memory: req.options?.use_memory ?? true,
            remember_results: req.options?.remember_results ?? false,
            auto_remember_threshold: req.options?.auto_remember_threshold ?? 0.95,
            max_candidates: req.options?.max_candidates ?? 12,
            conversion_rate_blocker: req.options?.conversion_rate_blocker ?? 0,
            debug: Boolean(req.options?.debug),
        },
        feature: req.feature ?? 'match_products',
    };
    const resp = await fetch(`${getBase()}/v1/services/match-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/services/match-products ${resp.status}: ${detail}`);
    }
    return json as SmartMatchProductsResult;
}

// ─── /v1/services/match-supplier-products ───────────────────────────────────
//
// Same API as aiMatchProducts but matches against SUPPLIER context — pulls
// previous supplier_invoices/supplier_deliveries for previousOrders signal,
// and persists memory under entity_type='supplier_product'. Pass supplier_id
// instead of client_id.

export async function aiMatchSupplierProducts(
    req: SmartMatchProductsRequest & { supplier_id?: string | number | null },
): Promise<SmartMatchProductsResult> {
    const token = await getAiAssistentToken();
    const entityId =
        (req as any).supplier_id != null
            ? String((req as any).supplier_id)
            : req.client_id != null
                ? String(req.client_id)
                : null;
    const body = {
        process: req.process ?? 'supplier_invoice',
        entity_id: entityId,
        input: req.image
            ? { image_base64: req.image.base64, mime_type: req.image.mime_type ?? 'image/jpeg' }
            : { text: req.text ?? '' },
        manual: {
            products: req.manual?.products,
            saved_matches: req.manual?.saved_matches,
            known_units: req.manual?.known_units ?? [],
            candidates_by_line: req.manual?.candidates_by_line,
        },
        options: {
            extract_tier: req.options?.extract_tier ?? 'medium',
            match_tier: req.options?.match_tier ?? 'medium',
            use_memory: req.options?.use_memory ?? true,
            remember_results: req.options?.remember_results ?? false,
            auto_remember_threshold: req.options?.auto_remember_threshold ?? 0.95,
            max_candidates: req.options?.max_candidates ?? 12,
            conversion_rate_blocker: req.options?.conversion_rate_blocker ?? 0,
            debug: Boolean(req.options?.debug),
        },
        feature: req.feature ?? 'match_supplier_products',
    };
    const resp = await fetch(`${getBase()}/v1/services/match-supplier-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/services/match-supplier-products ${resp.status}: ${detail}`);
    }
    return json as SmartMatchProductsResult;
}

// ─── /v1/services/match-products-stream  (NDJSON streaming) ────────────────
//
// Same brain as aiMatchProducts but the server flushes an `extracted` event
// AS SOON AS the first LLM call returns — the UI can render the line table
// while the second LLM call (matching) is still running.  Net perceived
// latency is roughly halved for the user.
//
// Wire format: line-delimited JSON. One JSON object per line, with an
// `event` discriminator:
//     {"event":"extracted","extracted":{...},"memory_entity_type":"...","usage":[...]}
//     {"event":"matches","extracted":{...},"matches":[...],"usage":[...]}
//     {"event":"error","detail":"..."}

export interface SmartMatchExtractedEvent {
    event: 'extracted';
    extracted: ExtractedDocument;
    memory_entity_type?: string;
    usage?: any[];
}

export interface SmartMatchMatchesEvent {
    event: 'matches';
    extracted: ExtractedDocument;
    matches: SmartProductMatch[] | null;
    usage: any[];
    warning?: string;
    memory_entity_type?: string;
    trace?: Record<string, unknown>;
}

export interface SmartMatchErrorEvent {
    event: 'error';
    detail: string;
    status_code?: number;
}

export type SmartMatchStreamEvent =
    | SmartMatchExtractedEvent
    | SmartMatchMatchesEvent
    | SmartMatchErrorEvent;

export interface SmartMatchStreamHandlers {
    onExtracted?: (e: SmartMatchExtractedEvent) => void;
    onMatches?: (e: SmartMatchMatchesEvent) => void;
    onError?: (e: SmartMatchErrorEvent) => void;
    signal?: AbortSignal;
}

async function _streamMatchProducts(
    path: string,
    body: Record<string, unknown>,
    handlers: SmartMatchStreamHandlers,
): Promise<SmartMatchMatchesEvent | SmartMatchErrorEvent | null> {
    const token = await getAiAssistentToken();
    const resp = await fetch(`${getBase()}${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept:         'application/x-ndjson',
            Authorization:  `Bearer ${token}`,
        },
        body:   JSON.stringify(body),
        signal: handlers.signal,
    });

    if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => '');
        const errEvt: SmartMatchErrorEvent = {
            event:       'error',
            status_code: resp.status,
            detail:      text.slice(0, 400) || `HTTP ${resp.status}`,
        };
        handlers.onError?.(errEvt);
        return errEvt;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let lastResult: SmartMatchMatchesEvent | SmartMatchErrorEvent | null = null;

    const flushLine = (line: string) => {
        const trimmed = line.trim();
        if (!trimmed) return;
        let evt: SmartMatchStreamEvent;
        try {
            evt = JSON.parse(trimmed) as SmartMatchStreamEvent;
        } catch (parseErr) {
            handlers.onError?.({
                event:  'error',
                detail: `invalid NDJSON chunk: ${String(parseErr)}: ${trimmed.slice(0, 120)}`,
            });
            return;
        }
        switch (evt.event) {
            case 'extracted':
                handlers.onExtracted?.(evt);
                break;
            case 'matches':
                handlers.onMatches?.(evt);
                lastResult = evt;
                break;
            case 'error':
                handlers.onError?.(evt);
                lastResult = evt;
                break;
        }
    };

    try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (value && value.length > 0) {
                buffer += decoder.decode(value, { stream: !done });
                let idx = buffer.indexOf('\n');
                while (idx >= 0) {
                    flushLine(buffer.slice(0, idx));
                    buffer = buffer.slice(idx + 1);
                    idx = buffer.indexOf('\n');
                }
            }
            if (done) break;
        }
        // flush any tail without trailing newline
        if (buffer.trim()) flushLine(buffer);
    } finally {
        try {
            reader.releaseLock();
        } catch {
            /* ignore */
        }
    }
    return lastResult;
}

/** Streaming variant of {@link aiMatchProducts}.  Yields an `extracted` event
 *  early so the UI can paint the line table immediately.  Resolves with the
 *  final matches event (or error) once the stream ends. */
export async function aiMatchProductsStream(
    req: SmartMatchProductsRequest,
    handlers: SmartMatchStreamHandlers,
): Promise<SmartMatchMatchesEvent | SmartMatchErrorEvent | null> {
    const body = {
        process:   req.process ?? 'order',
        client_id: req.client_id != null ? String(req.client_id) : null,
        input: req.image
            ? { image_base64: req.image.base64, mime_type: req.image.mime_type ?? 'image/jpeg' }
            : { text: req.text ?? '' },
        manual: {
            products:           req.manual?.products,
            saved_matches:      req.manual?.saved_matches,
            known_units:        req.manual?.known_units ?? [],
            candidates_by_line: req.manual?.candidates_by_line,
        },
        options: {
            extract_tier:            req.options?.extract_tier ?? 'medium',
            match_tier:              req.options?.match_tier ?? 'medium',
            use_memory:              req.options?.use_memory ?? true,
            remember_results:        req.options?.remember_results ?? false,
            auto_remember_threshold: req.options?.auto_remember_threshold ?? 0.95,
            max_candidates:          req.options?.max_candidates ?? 12,
            conversion_rate_blocker: req.options?.conversion_rate_blocker ?? 0,
            debug:                   Boolean(req.options?.debug),
        },
        feature: req.feature ?? 'match_products_stream',
    };
    return _streamMatchProducts('/v1/services/match-products-stream', body, handlers);
}

/** Streaming variant of {@link aiMatchSupplierProducts}. */
export async function aiMatchSupplierProductsStream(
    req: SmartMatchProductsRequest & { supplier_id?: string | number | null },
    handlers: SmartMatchStreamHandlers,
): Promise<SmartMatchMatchesEvent | SmartMatchErrorEvent | null> {
    const entityId =
        (req as any).supplier_id != null
            ? String((req as any).supplier_id)
            : req.client_id != null
                ? String(req.client_id)
                : null;
    const body = {
        process:   req.process ?? 'supplier_invoice',
        entity_id: entityId,
        input: req.image
            ? { image_base64: req.image.base64, mime_type: req.image.mime_type ?? 'image/jpeg' }
            : { text: req.text ?? '' },
        manual: {
            products:           req.manual?.products,
            saved_matches:      req.manual?.saved_matches,
            known_units:        req.manual?.known_units ?? [],
            candidates_by_line: req.manual?.candidates_by_line,
        },
        options: {
            extract_tier:            req.options?.extract_tier ?? 'medium',
            match_tier:              req.options?.match_tier ?? 'medium',
            use_memory:              req.options?.use_memory ?? true,
            remember_results:        req.options?.remember_results ?? false,
            auto_remember_threshold: req.options?.auto_remember_threshold ?? 0.95,
            max_candidates:          req.options?.max_candidates ?? 12,
            conversion_rate_blocker: req.options?.conversion_rate_blocker ?? 0,
            debug:                   Boolean(req.options?.debug),
        },
        feature: req.feature ?? 'match_supplier_products_stream',
    };
    return _streamMatchProducts('/v1/services/match-supplier-products-stream', body, handlers);
}

// ─── /v1/services/match-client  &  /v1/services/match-supplier ──────────────

export interface MatchEntityResult {
    entity_type: 'client' | 'supplier';
    entity_id: number | null;
    entity_name: string | null;
    business_name?: string | null;
    confidence: number;
    matched_field?: string | null;
    source: 'memory' | 'fuzzy' | null;
    memory_id?: string;
    alternatives?: Array<{
        entity_id: number;
        entity_name?: string;
        business_name?: string;
        score: number;
        matched_field?: string;
    }>;
}

async function _matchEntityCall(path: string, name: string, useMemory: boolean): Promise<MatchEntityResult> {
    const token = await getAiAssistentToken();
    const resp = await fetch(`${getBase()}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, use_memory: useMemory }),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent ${path} ${resp.status}: ${detail}`);
    }
    return json as MatchEntityResult;
}

export const aiMatchClient = (name: string, useMemory = true) =>
    _matchEntityCall('/v1/services/match-client', name, useMemory);

export const aiMatchSupplier = (name: string, useMemory = true) =>
    _matchEntityCall('/v1/services/match-supplier', name, useMemory);

// ─── /v1/services/match-document-type ───────────────────────────────────────

export type CanonicalDocType =
    | 'order' | 'delivery' | 'invoice' | 'credit_note' | 'receipt' | 'price_offer'
    | 'supplier_order' | 'supplier_delivery' | 'supplier_invoice'
    | string | null;

export interface MatchDocTypeResult {
    doc_type: CanonicalDocType;
    confidence: number;
    source: 'memory' | 'lexicon' | 'lexicon_contains' | 'fuzzy' | null;
    alternatives?: Array<{ doc_type: string; score: number }>;
    memory_id?: string;
}

export async function aiMatchDocumentType(text: string, useMemory = true): Promise<MatchDocTypeResult> {
    const token = await getAiAssistentToken();
    const resp = await fetch(`${getBase()}/v1/services/match-document-type`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text, use_memory: useMemory, feature: 'match_document_type' }),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/services/match-document-type ${resp.status}: ${detail}`);
    }
    return json as MatchDocTypeResult;
}

// ─── /v1/services/match/confirm  (persist user-approved mappings) ───────────

export interface MatchConfirmation {
    entity_type?: 'product' | 'supplier_product' | 'client' | 'supplier' | 'document_type';
    /** Override the per-entity scope; defaults to the request-level client_id/supplier_id. */
    scope_id?: string | number | null;
    req_name: string;
    req_unit?: string | null;
    matched_id: string | number;
    matched_name?: string;
    matched_unit?: string | null;
    metadata?: Record<string, unknown> | null;
}

export interface ConfirmOptions {
    /** Default scope when entity_type is product/supplier_product. */
    clientId?: string | number | null;
    supplierId?: string | number | null;
    /** "client" or "supplier" — affects defaulting of scope_id. */
    entityRole?: 'client' | 'supplier';
}

export async function aiConfirmMatches(
    confirmations: MatchConfirmation[],
    opts: ConfirmOptions = {},
): Promise<{ status: string; stored: number }> {
    const token = await getAiAssistentToken();
    const resp = await fetch(`${getBase()}/v1/services/match/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
            entity_role: opts.entityRole ?? 'client',
            client_id:   opts.clientId   != null ? String(opts.clientId)   : null,
            supplier_id: opts.supplierId != null ? String(opts.supplierId) : null,
            confirmations: confirmations.map((c) => ({
                ...c,
                matched_id: String(c.matched_id),
            })),
        }),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/services/match/confirm ${resp.status}: ${detail}`);
    }
    return json as { status: string; stored: number };
}

// ═══════════════════════════════════════════════════════════════════════════
//  Unified document extract + resolve (text / image / PDF)
//  /v1/services/document-extract  +  /v1/services/document-resolve
// ───────────────────────────────────────────────────────────────────────────
//  Two-step pipeline for ANY business document:
//    1. document-extract → unified JSON {doc_type, doc_id, allocation, date,
//                                        entity, totals, products[]}
//    2. document-resolve → fills in entity.id / products[].productId by
//                          running match_document_type / match_entity /
//                          match_products against the ERP. Optional.
//
//  Use when you receive a binary (image/PDF) OR want one unified schema for
//  every doc. For pure product-line text where you've already built
//  candidatesByLine locally, the legacy /product-extraction or /product-matching
//  endpoints are still cheaper.
// ═══════════════════════════════════════════════════════════════════════════

export interface DocFieldString {
    value: string | null;
    confidence: number;
}

export interface DocFieldEntity {
    role: 'client' | 'supplier' | null;
    name:    DocFieldString | null;
    id:      string | number | null;
    tax_id:  DocFieldString | null;
    phone:   DocFieldString | null;
    address: DocFieldString | null;
    /** Printed/customer/supplier number from source document, if different from ERP id. */
    external_id?: string | number | null;
    resolved?: {
        source: 'memory' | 'fuzzy' | 'forced' | 'exact' | null;
        confidence: number;
        matched_field?: string;
        matched_name?: string;
        alternatives?: Array<{
            entity_id: number; entity_name?: string;
            business_name?: string; score: number;
        }>;
    };
}

export interface DocTotals {
    net: number | null;
    vat: number | null;
    gross: number | null;
    currency: string | null;
    vat_rate: number | null;
}

export interface DocProductLine {
    lineId: string;
    productName: string;
    amount: number;
    unit: string | null;
    price: number | null;
    total: number | null;
    barcode: string | null;
    extId: string | null;
    rawText: string | null;
    confidence: number;
    // Populated by document-resolve (or aiDocumentExtract with resolve flag):
    productId?: number | null;
    matchUnit?: string | null;
    matchProductName?: string | null;
    matchSource?: 'memory' | 'llm' | 'barcode' | 'extId' | string | null;
    matchConfidence?: number | null;
    matchReason?: string | null;
    resolvedAmount?: number;
    resolvedPrice?: number;
}

export interface ExtractedDocumentSchema {
    doc_type:   (DocFieldString & { resolved?: { canonical: string | null; confidence: number; source: string | null } }) | null;
    doc_id:     DocFieldString | null;
    allocation: DocFieldString | null;
    date:       DocFieldString | null;
    entity:     DocFieldEntity;
    totals:     DocTotals;
    products:   DocProductLine[];
    warnings:   string[];
    resolve_warnings?: string[];
}

export interface DocExtractKnownEntity {
    role?: 'client' | 'supplier';
    id?: string | number;
    name?: string;
    tax_id?: string;
    phone?: string;
}

/** Identifies *our* company on the document so the LLM extracts the OTHER party as the entity. */
export interface DocExtractSelfCompany {
    /** Primary registered name (e.g. "גל רזית בע\"מ"). */
    name?: string;
    /** Our ע.מ / ח.פ. Helps when the document prints both parties' tax IDs. */
    tax_id?: string;
    /** Brand names, abbreviations, legal-name variants — anything the doc might print for us. */
    aliases?: string[];
    /** Internal/legacy id (rarely useful for the LLM but harmless). */
    id?: string;
}

export interface DocExtractHintsInput {
    /** What you EXPECT the doc to be — sharpens the model. 'auto' if unknown. */
    process?: 'auto' | 'order' | 'delivery' | 'invoice' | 'credit_note' | 'receipt'
            | 'price_offer' | 'supplier_order' | 'supplier_delivery' | 'supplier_invoice';
    entity_role?: 'client' | 'supplier';
    known_entity?: DocExtractKnownEntity;
    /** Who *we* are on the document — sender or recipient. Critical for client/supplier disambiguation. */
    self_company?: DocExtractSelfCompany;
    /** Subset of {doc_type,doc_id,allocation,date,entity,totals,products} — defaults to all. */
    expected_fields?: string[];
    known_units?: string[];
    language_hint?: string;
    /** Free-form prose hint about the company's domain (helps disambiguate). */
    company_context?: string;
    extra_instructions?: string;
}

export interface DocExtractOptionsInput {
    /** Vision benefits from 'smart'; pure text can use 'medium'. */
    tier?: AiTier;
    model?: string | null;
    debug?: boolean;
    feature?: string;
}

export interface DocExtractRequest {
    input: {
        text?: string;
        /** Single image as base64 (no `data:` prefix). */
        image?: { base64: string; mime_type?: string };
        /** Full PDF as base64 — auto paginated server-side (max 12 pages). */
        pdf?:   { base64: string };
    };
    hints?:   DocExtractHintsInput;
    options?: DocExtractOptionsInput;
}

export interface DocExtractResult {
    extracted: ExtractedDocumentSchema;
    usage: any[];
    trace?: Record<string, unknown>;
}

export async function aiDocumentExtract(req: DocExtractRequest): Promise<DocExtractResult> {
    const token = await getAiAssistentToken();
    const body = {
        input: {
            text:         req.input.text ?? null,
            image_base64: req.input.image?.base64 ?? null,
            mime_type:    req.input.image?.mime_type ?? 'image/jpeg',
            pdf_base64:   req.input.pdf?.base64 ?? null,
        },
        hints:   req.hints   ?? {},
        options: req.options ?? {},
    };
    const resp = await fetch(`${getBase()}/v1/services/document-extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/services/document-extract ${resp.status}: ${detail}`);
    }
    return json as DocExtractResult;
}


export interface DocResolveWantEntity {
    auto?: boolean;
    /** Skip the entity matcher entirely — use this id (UI/agent already knows). */
    force_id?: string | number | null;
}

export interface DocResolveWantProducts {
    auto?: boolean;
    /** Caller-built candidates per line (skips the server-side adapter pull). */
    manual_candidates?: Record<string, ExtractionCandidate[]>;
    saved_matches?: SavedExtractionMatch[];
    conversion_blocker?: number;
}

export interface DocResolveWant {
    match_doc_type?: boolean;
    entity?:   DocResolveWantEntity;
    products?: DocResolveWantProducts;
}

export interface DocResolveOptionsInput {
    use_memory?: boolean;
    remember_results?: boolean;
    extract_tier?: AiTier;
    match_tier?: AiTier;
    debug?: boolean;
    /** Override doc-type → process inference (e.g. force 'order' even if extract said 'invoice'). */
    process_override?: string | null;
    /** Override role (client/supplier) for the matchers. */
    entity_role?: 'client' | 'supplier';
    /** Strip unavailable products from candidates and stale memory (default false). */
    ignore_unavailable?: boolean;
    feature?: string;
}

export interface DocResolveRequest {
    extracted: ExtractedDocumentSchema;
    want?:    DocResolveWant;
    options?: DocResolveOptionsInput;
}

export type DocResolveResult = DocExtractResult;

export async function aiDocumentResolve(req: DocResolveRequest): Promise<DocResolveResult> {
    const token = await getAiAssistentToken();
    const body = {
        extracted: req.extracted,
        want:      req.want    ?? {},
        options:   req.options ?? {},
    };
    const resp = await fetch(`${getBase()}/v1/services/document-resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/services/document-resolve ${resp.status}: ${detail}`);
    }
    return json as DocResolveResult;
}

export interface AiDocumentRequest {
    input: DocExtractRequest['input'];
    hints?: DocExtractHintsInput;
    extract_options?: DocExtractOptionsInput;
    want?: DocResolveWant;
    resolve_options?: DocResolveOptionsInput;
    /** false = extract only, true/default = extract + resolve */
    resolve?: boolean;
}

/** One HTTP roundtrip: text/image/PDF -> extract -> optional resolve. */
export async function aiDocument(req: AiDocumentRequest): Promise<DocResolveResult> {
    const token = await getAiAssistentToken();
    const body = {
        input: {
            text:         req.input.text ?? null,
            image_base64: req.input.image?.base64 ?? null,
            mime_type:    req.input.image?.mime_type ?? 'image/jpeg',
            pdf_base64:   req.input.pdf?.base64 ?? null,
        },
        hints:           req.hints ?? {},
        extract_options: req.extract_options ?? {},
        want:            req.want ?? {},
        resolve_options: req.resolve_options ?? {},
        resolve:         req.resolve !== false,
    };
    const resp = await fetch(`${getBase()}/v1/services/document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/services/document ${resp.status}: ${detail}`);
    }
    return json as DocResolveResult;
}

/**
 * Convenience: extract → resolve with two explicit backend calls.
 *
 * Returns the resolved JSON with productIds / entity.id filled in.
 * Prefer `aiDocument` when you do not need to inspect the intermediate result.
 *
 * If you want to inspect intermediate extract output (e.g. show a confirmation
 * dialog before resolving), call `aiDocumentExtract` then `aiDocumentResolve`
 * separately.
 */
export async function aiDocumentExtractAndResolve(
    req: DocExtractRequest,
    want?: DocResolveWant,
    options?: DocResolveOptionsInput,
): Promise<DocResolveResult> {
    const extracted = await aiDocumentExtract(req);
    return aiDocumentResolve({
        extracted: extracted.extracted,
        want,
        options,
    });
}

// ─── /v1/services/product-extraction (LEGACY low-level — manual data only) ──

export async function aiProductExtraction(req: ProductExtractionRequest): Promise<ProductExtractionResult> {
    const token = await getAiAssistentToken();
    const body = {
        process: req.process,
        input: req.image
            ? {image_base64: req.image.base64, mime_type: req.image.mime_type ?? 'image/jpeg'}
            : {text: req.text ?? ''},
        knownUnits: req.knownUnits ?? [],
        candidatesByLine: req.candidatesByLine,
        savedMatches: req.savedMatches,
        conversionRateBlocker: req.conversionRateBlocker ?? 0,
        extractTier: req.extractTier ?? 'medium',
        matchTier: req.matchTier ?? 'medium',
        extractModel: req.extractModel ?? null,
        matchModel: req.matchModel ?? null,
        feature: req.feature ?? 'text_to_data',
        debug: Boolean(req.debug),
    };
    const resp = await fetch(`${getBase()}/v1/services/product-extraction`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/services/product-extraction ${resp.status}: ${detail}`);
    }
    return json as ProductExtractionResult;
}


// ════════════════════════════════════════════════════════════════════════════
//  DRAFTS API — POST /v1/drafts/* — Supabase-backed persistence layer.
// ────────────────────────────────────────────────────────────────────────────
//  Used by TextToDataDrawerAi to record every analyze action as a draft so
//  it shows up in the chat-iframe DraftsDrawer alongside drafts created from
//  the chat tab. Mirrors the iframe's `ai_assistent/frontend/src/api/client.ts`
//  surface — kept narrow on purpose: nexilcolab only needs to create + push
//  snapshots; full CRUD lives inside the chat embed.
// ════════════════════════════════════════════════════════════════════════════

export type AiDraftSource =
    | 'chat' | 'tab' | 'drawer' | 'whatsapp' | 'email' | 'scan' | 'api' | 'manual';

export type AiDraftStatus =
    | 'extracting' | 'resolving' | 'ready' | 'approved' | 'rejected' | 'failed';

export interface AiDraftFileInput {
    filename: string;
    mime_type: string;
    /** Base64 raw bytes (no `data:` prefix). */
    base64: string;
}

export interface AiDraftCreateRequest {
    source?: AiDraftSource;
    source_ref?: string | null;
    process?: string;
    entity_role?: 'client' | 'supplier' | null;
    hint_entity_id?: string | null;
    hint_entity_name?: string | null;
    text?: string | null;
    files?: AiDraftFileInput[];
    /** Default true — the backend kicks off extract+resolve in the background.
     *  When the caller is doing the streaming itself (TextToDataDrawerAi),
     *  set to false and PATCH snapshots in via `aiDraftsSnapshot`. */
    auto_run?: boolean;
}

export interface AiDraftSummary {
    id: string;
    public_no: number;
    company_id: string;
    created_by: string;
    source: AiDraftSource;
    input_kind: 'text' | 'image' | 'pdf' | 'mixed';
    process: string;
    entity_role: string;
    hint_entity_id?: string | null;
    hint_entity_name?: string | null;
    status: AiDraftStatus;
    totals?: any;
    created_at: string;
    updated_at: string;
}

export async function aiDraftsCreate(req: AiDraftCreateRequest): Promise<AiDraftSummary> {
    const token = await getAiAssistentToken();
    const resp = await fetch(`${getBase()}/v1/drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(req),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/drafts ${resp.status}: ${detail}`);
    }
    return json as AiDraftSummary;
}

/**
 * Push a client-computed extract/resolve snapshot onto an existing draft.
 *
 * TextToDataDrawerAi keeps the streaming /v1/services/document-* path for
 * live UI feedback. Each finished step is mirrored to the draft via this
 * call so the chat's DraftsDrawer can list/approve the draft afterwards.
 *
 * Status auto-infers: only `extracted` → 'resolving'; `resolved` → 'ready'.
 * Pass `status` to override (eg. 'failed' when the client step crashed).
 */
export async function aiDraftsSnapshot(
    draftId: string,
    body: {
        extracted?: any;
        resolved?: any;
        usage?: any[];
        status?: AiDraftStatus;
    },
): Promise<AiDraftSummary> {
    const token = await getAiAssistentToken();
    const resp = await fetch(`${getBase()}/v1/drafts/${encodeURIComponent(draftId)}/snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/drafts/${draftId}/snapshot ${resp.status}: ${detail}`);
    }
    return json as AiDraftSummary;
}

export async function aiDraftsList(opts?: {
    status?: string;
    process?: string;
    source?: string;
    limit?: number;
}): Promise<{items: AiDraftSummary[]}> {
    const token = await getAiAssistentToken();
    const q = new URLSearchParams();
    if (opts?.status) q.set('status', opts.status);
    if (opts?.process) q.set('process', opts.process);
    if (opts?.source) q.set('source', opts.source);
    q.set('limit', String(opts?.limit ?? 40));
    const resp = await fetch(`${getBase()}/v1/drafts?${q.toString()}`, {
        headers: {Authorization: `Bearer ${token}`},
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/drafts ${resp.status}: ${detail}`);
    }
    return json as {items: AiDraftSummary[]};
}

// ─── /v1/services/route-assign (נהג זמני from delivery address) ───────────────

export interface AiRouteAssignRequest {
    raw?: string;
    city?: string;
    street?: string;
    number?: string;
    lat?: number;
    lon?: number;
    client_id?: number;
    order_id?: string;
    current_agent_id?: number;
    source?: string;
    persist?: boolean;
    allow_llm?: boolean;
}

export interface AiRouteAssignResult {
    agent_id: number | null;
    area_id: string | null;
    method: string;
    confidence: number;
    reason: string;
    skip?: boolean;
    city?: string;
    street?: string;
    house_number?: string;
    raw_address?: string;
    assignment_id?: string;
}

export async function aiRouteAssign(body: AiRouteAssignRequest): Promise<AiRouteAssignResult> {
    const token = await getAiAssistentToken();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20_000);
    try {
        const resp = await fetch(`${getBase()}/v1/services/route-assign`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
                source: 'datasheet',
                persist: true,
                allow_llm: true,
                ...body,
            }),
            signal: controller.signal,
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) {
            const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
            throw new Error(`ai_assistent /v1/services/route-assign ${resp.status}: ${detail}`);
        }
        return json as AiRouteAssignResult;
    } finally {
        clearTimeout(timeout);
    }
}

export async function aiDraftsGet(draftId: string): Promise<any> {
    const token = await getAiAssistentToken();
    const resp = await fetch(`${getBase()}/v1/drafts/${encodeURIComponent(draftId)}`, {
        headers: {Authorization: `Bearer ${token}`},
    });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const detail = (json as any)?.detail || JSON.stringify(json).slice(0, 400);
        throw new Error(`ai_assistent /v1/drafts/${draftId} ${resp.status}: ${detail}`);
    }
    return json;
}

